import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

/**
 * 参数示例:
 * ```typescript
 * {
 *   "prompt": "A serene landscape with mountains and a lake at sunset",
 *   "size": "1024x1024",
 *   "n": 1,
 *   "quality": "high",
 *   "output_compression": 100
 * }
 * ```
 */
export interface MicrosoftGptImage1Params {
  /**
   * 图像生成提示词（必填），描述要生成的图像内容
   */
  prompt: string;
  /**
   * 图像尺寸（可选），支持: 1024x1024（方形，默认）, 1024x1536（竖屏）, 1536x1024（横屏）
   * @default "1024x1024"
   * @values 1024x1024 | 1024x1536 | 1536x1024
   */
  size?: '1024x1024' | '1024x1536' | '1536x1024';
  /**
   * 生成图像数量（可选），范围: 1-10，默认: 1
   * @default 1
   * @range 1 - 10
   */
  n?: number;
  /**
   * 图像质量（可选），支持: low（低）, medium（中）, high（高，默认）
   * @default "high"
   * @values low | medium | high
   */
  quality?: 'low' | 'medium' | 'high';
  /**
   * 输出压缩级别（可选），范围: 0-100，默认: 100（无压缩）
   * @default 100
   * @range 0 - 100
   */
  output_compression?: number;
}

export interface MicrosoftGptImage1Resource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * microsoft_gpt_image_1
 *
 * Microsoft Azure OpenAI GPT-Image-1 文本生成图像接口，支持生成高质量图像。
 * 
 * Model: gpt-image-1
 * Features:
 * - 支持三种图像尺寸：1024x1024（方形）、1024x1536（竖屏）、1536x1024（横屏）
 * - 支持三种质量级别：low（低）、medium（中）、high（高）
 * - 支持批量生成（1-10张）
 * - 支持压缩级别控制（0-100）
 * - API返回Base64格式，系统自动处理并上传到云存储
 * - 同步生成，立即返回结果
 *
 * @example
 * ```typescript
 * import { initSeacloud, microsoftGptImage1 } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await microsoftGptImage1(
 *   {
 *     "prompt": "A serene landscape with mountains and a lake at sunset",
 *     "size": "1024x1024",
 *     "n": 1,
 *     "quality": "high",
 *     "output_compression": 100
 *   }
 * );
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 MicrosoftGptImage1Params 接口定义
 * @returns 生成的资源列表
 */
export async function microsoftGptImage1(
  params: MicrosoftGptImage1Params,
): Promise<MicrosoftGptImage1Resource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'microsoft_gpt_image_1',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: MicrosoftGptImage1Resource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
